import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  App,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Tooltip,

} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { policyAPI, deviceAPI, preferenceAPI } from '../services/api';
import { useAuth } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { useTranslation } from 'react-i18next';

const IS_CUSTOMER_BUILD = process.env.REACT_APP_BUILD_TARGET === 'customer';
const isTenantAdminRole = IS_CUSTOMER_BUILD ? () => false : (role) => role === 'tenant_admin';

const { Title, Text } = Typography;
const { Option, OptGroup } = Select;
const { TextArea } = Input;

const PolicyManagement = () => {
  const { message, modal } = App.useApp();
  const { user } = useAuth();
  const { t } = useTranslation();
  // 策略相关状态
  const [policies, setPolicies] = useState([]);
  const [deviceGroups, setDeviceGroups] = useState([]);
  const [devicePlatformCounts, setDevicePlatformCounts] = useState({});
  // 移除rules状态，改为直接输入模式
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [statsModalVisible, setStatsModalVisible] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [policyStats, setPolicyStats] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [userPreferences, setUserPreferences] = useState(null);
  // 批量删除相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);
  const hasInitializedRef = useRef(false);

  const loadPolicies = useCallback(async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        search: search || searchText
      };
      const response = await policyAPI.getPolicies(params);
      if (response.data && response.data.data && response.data.data.items) {
        const policyList = Array.isArray(response.data.data.items) ? response.data.data.items : [];
        // 清理策略数据，避免循环引用
        const cleanPolicyList = policyList.map(policy => ({
          id: policy.id,
          name: policy.name,
          description: policy.description,
          status: policy.status,
          priority: policy.priority,
          application_path: policy.application_path,
          application_name: policy.application_name,
          file_hash: policy.file_hash,
          macos_identifier: policy.macos_identifier,
          publisher: policy.publisher,
          process_name: policy.process_name,
          scope_type: policy.scope_type,
          scope_devices: policy.scope_devices,
          scope_groups: policy.scope_groups,
          device_groups: policy.device_groups ? policy.device_groups.map(group => ({
            id: group.id,
            name: group.name,
            device_ids: group.device_ids
          })) : [],
          device_group_ids: policy.device_group_ids,
          rules: policy.rules ? policy.rules.map(rule => ({
            id: rule.id,
            name: rule.name,
            type: rule.type
          })) : [],
          rule_ids: policy.rule_ids,
          created_at: policy.created_at,
          updated_at: policy.updated_at
        }));
        setPolicies(cleanPolicyList);
        // 更新分页信息
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.data.total || 0
        }));
      } else {
        setPolicies([]);
        setPagination(prev => ({ ...prev, total: 0 }));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('policy.message.load_failed', );
      message.error(t('policy.message.load_failed_with_reason', { error: errorMessage }));
    } finally {
      setLoading(false);
    }
  }, [message, searchText, t]);

  const loadDevicePlatformStats = useCallback(async () => {
    try {
      const response = await deviceAPI.getDeviceStats();
      const platformRows = response.data?.data?.by_platform || [];
      const nextCounts = {};
      if (Array.isArray(platformRows)) {
        platformRows.forEach((row) => {
          const platform = String(row.platform || '').toLowerCase();
          if (platform) {
            nextCounts[platform] = Number(row.count || 0);
          }
        });
      }
      setDevicePlatformCounts(nextCounts);
    } catch (error) {
      console.error('加载设备平台统计失败:', error);
      setDevicePlatformCounts({});
    }
  }, []);

  const loadDeviceGroups = useCallback(async () => {
    try {
      const response = await deviceAPI.getDeviceGroupStats();
      const stats = response.data?.data || {};
      const allGroups = [
        ...(Array.isArray(stats.system_groups) ? stats.system_groups : []),
        ...(Array.isArray(stats.custom_groups) ? stats.custom_groups : []),
      ];
      setDeviceGroups(allGroups);
    } catch (error) {
      console.error('加载设备群组失败:', error);
      message.error('加载设备群组失败');
      setDeviceGroups([]);
    }
  }, [message]);

  // 加载用户偏好设置
  const loadUserPreferences = useCallback(async () => {
    try {
      const response = await preferenceAPI.getUserPreferences();
      if (response.data && response.data.data) {
        const preferences = response.data.data;
        setUserPreferences(preferences);
        
        // 从用户偏好中读取分页设置
        if (preferences.settings) {
          try {
            const settings = JSON.parse(preferences.settings);
            if (settings.policyPageSize && [10, 20, 50, 100, 500, 1000].includes(settings.policyPageSize)) {
              setPagination(prev => ({
                ...prev,
                pageSize: settings.policyPageSize
              }));
              return settings.policyPageSize;
            }
          } catch (e) {
            console.warn('解析用户偏好设置失败:', e);
          }
        }
      }
    } catch (error) {
      console.warn('加载用户偏好设置失败:', error);
    }
    return 10; // 默认分页大小
  }, []);

  // 保存分页设置到用户偏好
  const savePaginationPreference = useCallback(async (pageSize) => {
    try {
      if (!userPreferences) return;
      
      let settings = {};
      if (userPreferences.settings) {
        try {
          settings = JSON.parse(userPreferences.settings);
        } catch (e) {
          console.warn('解析现有设置失败:', e);
        }
      }
      
      settings.policyPageSize = pageSize;
      
      await preferenceAPI.updateUserPreferences({
        ...userPreferences,
        settings: JSON.stringify(settings)
      });
    } catch (error) {
      console.warn('保存分页偏好设置失败:', error);
    }
  }, [userPreferences]);

  // 移除loadRules函数，改为直接输入模式

  useEffect(() => {
    let isMounted = true;
    const initializeData = async () => {
      const pageSize = await loadUserPreferences();
      if (!isMounted) {
        return;
      }
      setPagination(prev => ({ ...prev, current: 1, pageSize }));
      await Promise.all([
        loadPolicies(1, pageSize, ''),
        loadDevicePlatformStats()
      ]);
      if (isMounted) {
        hasInitializedRef.current = true;
      }
    };
    initializeData();
    return () => {
      isMounted = false;
    };
  }, [loadUserPreferences, loadDevicePlatformStats, loadPolicies]);



  const ensureScopeGroupsLoaded = useCallback(async () => {
    if (deviceGroups.length > 0) {
      return;
    }
    await loadDeviceGroups();
  }, [deviceGroups.length, loadDeviceGroups]);

  const handleAdd = async () => {
    await ensureScopeGroupsLoaded();
    setEditingPolicy(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (policy) => {
    await ensureScopeGroupsLoaded();
    const detailResponse = await policyAPI.getPolicy(policy.id);
    const detailPolicy = detailResponse?.data?.data || policy;
    console.log('PolicyManagement: 编辑策略，原始数据:', detailPolicy);
    // 只传递必要的字段，避免循环引用
    const cleanPolicy = {
      id: detailPolicy.id,
      name: detailPolicy.name,
      description: detailPolicy.description,
      status: detailPolicy.status,
      priority: detailPolicy.priority,
      application_path: detailPolicy.application_path,
      application_name: detailPolicy.application_name,
      file_hash: detailPolicy.file_hash,
      macos_identifier: detailPolicy.macos_identifier,
      publisher: detailPolicy.publisher,
      process_name: detailPolicy.process_name,
      device_groups: detailPolicy.device_groups,
      scope_groups: detailPolicy.scope_groups,
      device_group_ids: detailPolicy.device_group_ids,
      rules: detailPolicy.rules
    };
    setEditingPolicy(cleanPolicy);
    
    // 处理设备群组数据，支持多种数据格式
    let scopeGroups = [];
    if (detailPolicy.device_groups && Array.isArray(detailPolicy.device_groups)) {
      // 如果device_groups是对象数组，提取id
      scopeGroups = detailPolicy.device_groups.map(g => typeof g === 'object' ? `group_${g.id}` : `group_${g}`);
      console.log('PolicyManagement: 从device_groups提取群组:', scopeGroups);
    } else if (detailPolicy.scope_groups && Array.isArray(detailPolicy.scope_groups)) {
      // 如果scope_groups是字符串数组
      scopeGroups = detailPolicy.scope_groups;
      console.log('PolicyManagement: 从scope_groups提取群组:', scopeGroups);
    } else if (detailPolicy.device_group_ids && Array.isArray(detailPolicy.device_group_ids)) {
      // 如果device_group_ids是ID数组
      scopeGroups = detailPolicy.device_group_ids.map(id => `group_${id}`);
      console.log('PolicyManagement: 从device_group_ids提取群组:', scopeGroups);
    }
    console.log('PolicyManagement: 最终群组数据:', scopeGroups);
    
    form.setFieldsValue({
        name: detailPolicy.name,
        description: detailPolicy.description,
        status: detailPolicy.status || 'active',
        scope_groups: scopeGroups,
        application_path: detailPolicy.application_path,
        publisher: detailPolicy.publisher,
        application_name: detailPolicy.application_name,
        file_hash: detailPolicy.file_hash,
        process_name: detailPolicy.process_name,
        macos_identifier: detailPolicy.macos_identifier,
      });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // 处理设备组数据格式
      const scopeGroups = values.scope_groups || [];
      // 为向后兼容，device_group_ids需要移除group_前缀
      const deviceGroupIds = scopeGroups.map(item => {
        if (item.startsWith('group_')) {
          return item.substring(6); // 移除'group_'前缀
        }
        return item;
      }).filter(item => item !== 'no_devices'); // 过滤掉特殊值
      
      // 准备策略数据
      const policyData = {
          name: values.name,
          description: values.description,
          status: values.status || 'active',
          scope_groups: scopeGroups,
          device_group_ids: deviceGroupIds, // 向后兼容，使用纯ID
          application_path: values.application_path,
          publisher: values.publisher,
          application_name: values.application_name,
          file_hash: values.file_hash,
          process_name: values.process_name,
          macos_identifier: values.macos_identifier,
        };

      if (editingPolicy) {
        await policyAPI.updatePolicy(editingPolicy.id, policyData);
        message.success(t('policy.save.update_success', ));
      } else {
        await policyAPI.createPolicy(policyData);
        message.success(t('policy.save.create_success', ));
      }
      
      setModalVisible(false);
      form.resetFields();
      setEditingPolicy(null);
      // 保持当前分页状态重新加载数据
      loadPolicies(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      console.error('策略操作失败:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '策略操作失败';
      message.error(t('policy.message.operation_failed', { error: errorMessage }));
    }
  };

  const handleDelete = async (id) => {
    try {
      const policy = policies.find(p => p.id === id);
      if (policy && policy.status === 'active') {
        message.error(t('policy.delete.must_inactive', ));
        return;
      }
      
      await policyAPI.deletePolicy(id);
      message.success(t('policy.message.delete_success', ));
      // 保持当前分页状态重新加载数据
      loadPolicies(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '删除失败';
      message.error(t('policy.message.delete_failed', { error: errorMessage }));
    }
  };

  // 批量删除处理函数
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('policy.batch_delete.select_warning', ));
      return;
    }

    // 检查选中的策略是否都是禁用状态
    const selectedPolicies = policies.filter(p => selectedRowKeys.includes(p.id));
    const activePolicies = selectedPolicies.filter(p => p.status === 'active');
    
    if (activePolicies.length > 0) {
      const names = activePolicies.map(p => p.name).join(', ');
      message.error(t('policy.batch_delete.active_restriction', { names }));
      return;
    }

    modal.confirm({
      title: t('policy.batch_delete.modal.title', ),
      content: t('policy.batch_delete.modal.content', { count: selectedRowKeys.length }),
      okText: t('policy.batch_delete.modal.okText', ),
      okType: 'danger',
      cancelText: t('common.cancel', ),
      onOk: async () => {
        setBatchDeleteLoading(true);
        try {
          await policyAPI.batchDeletePolicies(selectedRowKeys);
          message.success(t('policy.batch_delete.success', { count: selectedRowKeys.length }));
          setSelectedRowKeys([]);
          // 保持当前分页状态重新加载数据
          loadPolicies(pagination.current, pagination.pageSize, searchText);
        } catch (error) {
          const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('policy.batch_delete.failed', );
          message.error(t('policy.batch_delete.failed_with_reason', { error: errorMessage }));
        } finally {
          setBatchDeleteLoading(false);
        }
      }
    });
  };

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (record) => {
      // 只有禁用状态的策略才能被选择
      // L0系统策略不能被选择
      const isL0SystemPolicy = record.id && record.id.startsWith('l0-');
      const userRole = user?.role;
      const canDelete = userRole === 'admin' || isTenantAdminRole(userRole) || userRole === 'super_admin';
      
      return {
        disabled: record.status === 'active' || isL0SystemPolicy || !canDelete,
      };
    },
  };

  const handleStatusChange = async (policyId, enabled) => {
    try {
      const policy = policies.find(p => p.id === policyId);
      if (!policy) {
        message.error(t('policy.message.not_found', ));
        return;
      }
      
      // 只传递必要的字段，避免循环引用
      const updatedPolicy = {
        id: policy.id,
        name: policy.name,
        description: policy.description,
        status: enabled ? 'active' : 'inactive',
        priority: policy.priority,
        device_group_ids: policy.device_groups?.map(g => g.id) || [],
        rule_ids: policy.rules?.map(r => r.id) || [],
        // 只包含基本字段，避免包含可能的DOM元素或React组件引用
        application_path: policy.application_path,
        application_name: policy.application_name,
        file_hash: policy.file_hash,
        macos_identifier: policy.macos_identifier,
        publisher: policy.publisher,
        process_name: policy.process_name
      };
      
      await policyAPI.updatePolicy(policyId, updatedPolicy);
      if (enabled) {
        message.success(t('policy.status.activate_success', ));
      } else {
        message.success(t('policy.status.deactivate_success', ));
      }
      // 保持当前分页状态重新加载数据
      loadPolicies(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      console.error('状态切换失败:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('policy.status.update_failed', );
      message.error(t('policy.status.update_failed', { error: errorMessage }));
    }
  };

  // // 重新推送策略处理函数
  // const handleRepushPolicy = async (policyId) => {
  //   try {
  //     const policy = policies.find(p => p.id === policyId);
  //     if (!policy) {
  //       message.error('策略不存在');
  //       return;
  //     }

  //     if (policy.status !== 'active') {
  //       message.error('只能重新推送已启用的策略');
  //       return;
  //     }

  //     modal.confirm({
  //       title: '确认重新推送策略',
  //       content: `确定要重新推送策略 "${policy.name}" 到所有关联设备吗？`,
  //       okText: '确定推送',
  //       cancelText: '取消',
  //       onOk: async () => {
  //         try {
  //           await policyAPI.repushPolicy(policyId);
  //           message.success('策略重新推送成功，正在向设备推送...');
  //           // 重新加载策略列表以更新推送状态
  //           loadPolicies(pagination.current, pagination.pageSize, searchText);
  //         } catch (error) {
  //           console.error('重新推送失败:', error);
  //           const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '重新推送失败';
  //           message.error('重新推送失败: ' + errorMessage);
  //         }
  //       }
  //     });
  //   } catch (error) {
  //     console.error('重新推送策略失败:', error);
  //     message.error('重新推送策略失败');
  //   }
  // };

  const handleShowStats = async (policy) => {
    try {
      const response = await policyAPI.getPolicyDetailStats(policy.id);
      setPolicyStats(response.data.data);
      // 只传递必要的字段，避免循环引用
      const cleanPolicy = {
        id: policy.id,
        name: policy.name,
        description: policy.description,
        status: policy.status,
        priority: policy.priority,
        application_path: policy.application_path,
        application_name: policy.application_name,
        file_hash: policy.file_hash,
        macos_identifier: policy.macos_identifier,
        publisher: policy.publisher,
        process_name: policy.process_name,
        device_groups: policy.device_groups?.map(g => ({ id: g.id, name: g.name })) || [],
        rules: policy.rules?.map(r => ({ id: r.id, name: r.name })) || []
      };
      setSelectedPolicy(cleanPolicy);
      setStatsModalVisible(true);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('policy.message.stats_load_failed', );
      message.error(t('policy.message.stats_load_failed', { error: errorMessage }));
    }
  };

  const getScopeDeviceCount = (policy) => {
    // 检查是否为L0系统策略
    const isL0SystemPolicy = policy.id && policy.id.startsWith('l0-');
    
    if (isL0SystemPolicy) {
      // L0系统策略根据平台计算对应设备数量
      let platform = '';
      if (policy.id.includes('macos')) platform = 'macos';
      else if (policy.id.includes('windows')) platform = 'windows';
      else if (policy.id.includes('linux')) platform = 'linux';
      
      return devicePlatformCounts[platform] || 0;
    }
    
    if (policy.scope_type === 'groups') {
      return (policy.scope_groups || []).reduce((total, groupId) => {
        const group = deviceGroups.find(g => g.id === groupId);
        return total + Number(group?.device_count || 0);
      }, 0);
    } else {
      return (policy.scope_devices || []).length;
    }
  };

  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;
    
    // 如果分页大小改变，保存到用户偏好
    if (pageSize !== pagination.pageSize) {
      savePaginationPreference(pageSize);
    }
    
    loadPolicies(current, pageSize, searchText);
  };

  // 搜索时重置分页并重新加载数据
  useEffect(() => {
    if (!hasInitializedRef.current) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      loadPolicies(1, pagination.pageSize, searchText);
    }, 300); // 防抖处理
    
    return () => clearTimeout(timeoutId);
  }, [searchText, loadPolicies, pagination.pageSize]);

  // 搜索现在由后端处理，不需要前端过滤

  const columns = [
    {
      title: t('policy.table.columns.name', ),
      dataIndex: 'name',
      key: 'name',
      width: 300,
      sorter: (a, b) => String(a?.name || '').localeCompare(String(b?.name || '')),
      ellipsis: false,
      render: (text, record) => (
        <div style={{ maxWidth: '280px', wordWrap: 'break-word', wordBreak: 'break-all' }}>
          <Text strong style={{ display: 'block', whiteSpace: 'normal' }}>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', whiteSpace: 'normal' }}>
            {record.description}
          </Text>
        </div>
      ),
    },

    {
      title: t('policy.table.columns.status', ),
      key: 'status',
      width: 100,
      sorter: (a, b) => String(a?.status || '').localeCompare(String(b?.status || '')),
      render: (_, record) => {
        // L0系统策略使用status字段，普通策略默认为active
        const isL0SystemPolicy = record.id && record.id.startsWith('l0-');
        const status = isL0SystemPolicy ? record.status : (record.status || 'active');
        const isActive = status === 'active';
        
        return (
          <Switch
            checked={isActive}
            checkedChildren={t('policy.status.enable_label', )}
            unCheckedChildren={t('policy.status.disable_label', )}
            onChange={(checked) => handleStatusChange(record.id, checked)}
            disabled={isL0SystemPolicy}
          />
        );
      },
    },
    {
      title: t('policy.table.columns.scope', ),
      key: 'scope',
      width: 200,
      sorter: (a, b) => {
        const aCount = Array.isArray(a?.device_groups) ? a.device_groups.length : 0;
        const bCount = Array.isArray(b?.device_groups) ? b.device_groups.length : 0;
        return aCount - bCount;
      },
      render: (_, record) => {
        const isL0SystemPolicy = record.id && record.id.startsWith('l0-');
        
        if (isL0SystemPolicy) {
          let platformText = '';
          if (record.id.includes('macos')) platformText = t('policy.scope.platform.macos_device', );
          else if (record.id.includes('windows')) platformText = t('policy.scope.platform.windows_device', );
          else if (record.id.includes('linux')) platformText = t('policy.scope.platform.linux_device', );
          else platformText = t('policy.scope.platform.system_device', );
          
          const deviceCount = getScopeDeviceCount(record);
          return (
            <div>
              <Tag color="purple">
                {t('policy.scope.system_device_tag', { count: deviceCount, platform: platformText })}
              </Tag>
            </div>
          );
        } else {
          const groupCount = record.device_groups ? record.device_groups.length : 0;
          const deviceCount = getScopeDeviceCount(record);
          
          return (
            <div>
              <div style={{ marginBottom: 4 }}>
                <Tag color="blue">{t('policy.scope.group_count', { count: groupCount })}</Tag>
              </div>
              <div style={{ marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('policy.scope.total_devices', { count: deviceCount })}
                </Text>
              </div>
            </div>
          );
        }
      },
     },
     {
      title: t('policy.table.columns.created_at', ),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      sorter: (a, b) => new Date(a?.created_at || 0).getTime() - new Date(b?.created_at || 0).getTime(),
      render: (time) => formatDate(time),
    },
    {
      title: t('policy.table.columns.actions', ),
      key: 'actions',
      width: 150,
      render: (_, record) => {
        // 检查是否为L0系统策略
        const isL0SystemPolicy = record.id && record.id.startsWith('l0-');
        
        // 检查用户权限
        const userRole = user?.role;
        const canEdit = userRole === 'user' || userRole === 'admin' || isTenantAdminRole(userRole) || userRole === 'super_admin';
        const canDelete = userRole === 'admin' || isTenantAdminRole(userRole) || userRole === 'super_admin';
        const isAuditor = userRole === 'auditor';
        
        return (
          <Space>
            {!isL0SystemPolicy && canEdit && (
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                {t('common.edit', )}
              </Button>
            )}
            <Button
              type="link"
              icon={<BarChartOutlined />}
              onClick={() => handleShowStats(record)}
            >
              {t('policy.actions.stats.tooltip', )}
            </Button>
            {!isL0SystemPolicy && canDelete && record.status !== 'active' && (
              <Popconfirm
                title={t('policy.delete.confirm_title', )}
                onConfirm={() => handleDelete(record.id)}
                okText={t('common.ok', )}
                cancelText={t('common.cancel', )}
              >
                <Tooltip title={t('common.delete', )}>
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                  >
                    {t('common.delete', )}
                  </Button>
                </Tooltip>
              </Popconfirm>
            )}
            {!isL0SystemPolicy && canDelete && record.status === 'active' && (
              <Tooltip title={t('policy.delete.must_inactive', )}>
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  disabled
                >
                  {t('common.delete', )}
                </Button>
              </Tooltip>
            )}
            {isL0SystemPolicy && (
              <Tooltip title={t('policy.system_policy.not_editable', )}>
                <Tag color="blue">{t('policy.system_policy.tag', )}</Tag>
              </Tooltip>
            )}
            {isAuditor && (
              <Tag color="green">{t('common.view_only', )}</Tag>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Title level={3} style={{ margin: 0 }}>{t('policy.page.title', )}</Title>
                <Text type="secondary">{t('policy.page.description', )}</Text>
              </div>
              <Space>
                <Input
                  placeholder={t('policy.search.placeholder', )}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={<SearchOutlined />}
                  style={{ width: 200 }}
                  allowClear
                />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => loadPolicies(pagination.current, pagination.pageSize, searchText)}
                  loading={loading}
                >
                  {t('common.actions.refresh', )}
                </Button>
                {user?.role !== 'auditor' && selectedRowKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleBatchDelete}
                    loading={batchDeleteLoading}
                  >
                    {t('policy.batch_delete.button', )} ({selectedRowKeys.length})
                  </Button>
                )}
                {user?.role !== 'auditor' && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                  >
                    {t('policy.actions.create', )}
                  </Button>
                )}
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={policies}
              rowKey="id"
              loading={loading}
              scroll={{ x: 'max-content' }}
              tableLayout="auto"
              rowSelection={user?.role !== 'auditor' ? rowSelection : undefined}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => t('policy.pagination.show_total', { start: range[0], end: range[1], total }),
                pageSizeOptions: ['10', '20', '50', '100', '500', '1000'],
              }}
              onChange={handleTableChange}
            />
          </Card>
        </Col>
      </Row>

      {/* 添加/编辑策略模态框 */}
      <Modal
        title={editingPolicy ? t('policy.modal.edit.title', ) : t('policy.modal.create.title', )}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingPolicy(null);
        }}
        onOk={() => form.submit()}
        width={900}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: 'active',
            scope_groups: []
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('policy.form.name.label', )}
                rules={[
                  { required: true, message: t('policy.form.name.required', ) },
                  { max: 100, message: t('policy.form.name.max', ) },
                  { min: 2, message: t('policy.form.name.min', ) },
                  { pattern: /^[\u4e00-\u9fa5a-zA-Z0-9\s_-]+$/, message: t('policy.form.name.pattern_error', ) }
                ]}
              >
                <Input placeholder={t('policy.form.name.placeholder', )} maxLength={100} showCount />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label={t('policy.form.status.label', )}
                valuePropName="checked"
                getValueFromEvent={(checked) => checked ? 'active' : 'inactive'}
                getValueProps={(value) => ({ checked: value === 'active' })}
              >
                <Switch
                  checkedChildren={t('policy.status.enable_label', )}
                  unCheckedChildren={t('policy.status.disable_label', )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label={t('policy.form.description.label', )}
          >
            <TextArea
              rows={3}
              placeholder={t('policy.form.description.placeholder', )}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="scope_groups"
            label={t('policy.form.scope_groups.label', )}
            rules={[{ required: true, message: t('policy.form.scope_groups.required', ) }]}
            tooltip={t('policy.form.scope_groups.tooltip', )}
          >
            <Select
              mode="multiple"
              placeholder={t('policy.form.scope_groups.placeholder', )}
              showSearch
              allowClear
              filterOption={(input, option) => {
                const label = option?.label || option?.children || '';
                return String(label).toLowerCase().includes(input.toLowerCase());
              }}
            >
              {/* 未选择设备选项 */}
              <Option key="no_devices" value="no_devices">
                {t('policy.form.scope_groups.option.no_devices', )}
              </Option>
              
              {/* 系统默认群组 */}
              <OptGroup label={t('policy.form.scope_groups.optgroup.system_default', )}>
                {deviceGroups
                  .filter(group => group.is_default || group.is_system)
                  .map(group => (
                    <Option key={`group_${group.id}`} value={`group_${group.id}`}>
                      {`📁 ${group.name} (${group.device_count || 0}${t('policy.scope.device_unit', )})`}
                    </Option>
                  ))}
              </OptGroup>
              
              {/* 用户创建的群组 */}
              <OptGroup label={t('policy.form.scope_groups.optgroup.user_groups', )}>
                {deviceGroups
                  .filter(group => !group.is_default && !group.is_system)
                  .map(group => (
                    <Option key={`group_${group.id}`} value={`group_${group.id}`}>
                      {`📁 ${group.name} (${group.device_count || 0}${t('policy.scope.device_unit', )})`}
                    </Option>
                  ))}
              </OptGroup>
            </Select>
          </Form.Item>

          {/* 应用程序信息 */}
          <Form.Item
            name="application_path"
            label={t('policy.form.application_path.label', )}
            tooltip={
              <div>
                <div><strong>{t('policy.tooltip.common.get_method_label', )}</strong></div>
                <div><strong>{t('policy.tooltip.platform.macos_label', )}</strong> {t('policy.tooltip.application_path.macos_text', )}<br/>
                <code>find /Applications -name "*.app" -type d</code></div>
                <div><strong>{t('policy.tooltip.platform.windows_label', )}</strong> {t('policy.tooltip.application_path.windows_text', )}</div>
                <div><strong>{t('policy.tooltip.platform.linux_label', )}</strong> <code>{t('policy.tooltip.application_path.linux_command_which', )}</code> {t('common.or', )} <code>{t('policy.tooltip.application_path.linux_command_whereis', )}</code></div>
                <div><strong>{t('policy.tooltip.common.example_label', )}</strong> Safari, chrome.exe, firefox</div>
              </div>
            }
          >
            <Input placeholder={t('policy.form.application_path.placeholder', )} />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="publisher"
                label={t('policy.form.publisher.label', )}
                tooltip={
                  <div>
                    <div><strong>{t('policy.tooltip.common.get_method_label', )}</strong></div>
                    <div><strong>{t('policy.tooltip.platform.macos_label', )}</strong> {t('policy.tooltip.publisher.macos_text', )}<br/>
                    <code>codesign -dv /Applications/app-name.app</code><br/>
                    {t('policy.tooltip.publisher.macos_find_team', )}</div>
                    <div><strong>{t('policy.tooltip.platform.windows_label', )}</strong> {t('policy.tooltip.publisher.windows_text', )}</div>
                    <div><strong>{t('policy.tooltip.platform.linux_label', )}</strong> <code>dpkg -S package</code> {t('common.or', )} {t('policy.tooltip.publisher.linux_text', )}</div>
                    <div><strong>{t('policy.tooltip.common.example_label', )}</strong> UBF8T346G9, Microsoft Corporation, Apple Inc.</div>
                  </div>
                }
              >
                <Input placeholder={t('policy.form.publisher.placeholder', )} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="application_name"
                label={t('policy.form.application_name.label', )}
                tooltip={
                  <div>
                    <div><strong>{t('policy.tooltip.common.get_method_label', )}</strong></div>
                    <div>
                      <strong>{t('policy.tooltip.platform.macos_label', )}</strong> {t('policy.tooltip.application_name.macos_text', )}
                      <br/>
                      <code>mdls -name kMDItemDisplayName /Applications/app-name.app</code>
                    </div>
                    <div><strong>{t('policy.tooltip.platform.windows_label', )}</strong> {t('policy.tooltip.application_name.windows_text', )}</div>
                    <div><strong>{t('policy.tooltip.platform.linux_label', )}</strong> {t('policy.tooltip.application_name.linux_text', )} <code>ps aux | grep process-name</code></div>
                    <div><strong>{t('policy.tooltip.common.example_label', )}</strong> Safari, Google Chrome, Microsoft Edge</div>
                  </div>
                }
              >
                <Input placeholder={t('policy.form.application_name.placeholder', )} />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="macos_identifier"
                label={t('policy.form.macos_identifier.label', )}
                tooltip={
                  <div>
                    <div><strong>{t('policy.tooltip.common.get_method_label', )}</strong></div>
                    <div><strong>{t('policy.tooltip.platform.macos_label', )}</strong> {t('policy.tooltip.macos_identifier.macos_text', )}<br/>
                    <code>mdls -name kMDItemCFBundleIdentifier /Applications/app-name.app</code><br/>
                    {t('policy.tooltip.macos_identifier.or', )}<br/>
                    <code>osascript -e 'id of app "app-name"'</code></div>
                    <div><strong>{t('policy.tooltip.common.description_label', )}</strong> {t('policy.tooltip.macos_identifier.description', )}</div>
                    <div><strong>{t('policy.tooltip.common.example_label', )}</strong> com.apple.Safari, com.google.Chrome, com.microsoft.VSCode</div>
                  </div>
                }
              >
                <Input placeholder={t('policy.form.macos_identifier.placeholder', )} />
              </Form.Item>
            </Col>
            <Col span={12}>
              {/* 预留空间，保持布局平衡 */}
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="file_hash"
                label={t('policy.form.file_hash.label', )}
                tooltip={
                  <div>
                    <div><strong>{t('policy.tooltip.common.get_method_label', )}</strong></div>
                    <div><strong>{t('policy.tooltip.platform.macos_label', )}</strong> <code>shasum -a 256 /path/to/file</code></div>
                    <div><strong>{t('policy.tooltip.platform.windows_label', )}</strong> <code>certutil -hashfile "file path" SHA256</code></div>
                    <div><strong>{t('policy.tooltip.platform.linux_label', )}</strong> <code>sha256sum /path/to/file</code></div>
                    <div><strong>{t('policy.tooltip.common.description_label', )}</strong> {t('policy.tooltip.file_hash.description', )}</div>
                  </div>
                }
              >
                <Input placeholder={t('policy.form.file_hash.placeholder', )} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="process_name"
                label={t('policy.form.process_name.label', )}
                tooltip={
                  <div>
                    <div><strong>{t('policy.tooltip.common.get_method_label', )}</strong></div>
                    <div><strong>{t('policy.tooltip.platform.macos_label', )}</strong> <code>ps aux | grep app-name</code> {t('policy.tooltip.process_name.macos_text', )}</div>
                    <div><strong>{t('policy.tooltip.platform.windows_label', )}</strong> {t('policy.tooltip.process_name.windows_text', )}</div>
                    <div><strong>{t('policy.tooltip.platform.linux_label', )}</strong> <code>ps aux | grep app-name</code> {t('common.or', )} <code>top</code></div>
                    <div><strong>{t('policy.tooltip.common.example_label', )}</strong> Safari, chrome.exe, firefox</div>
                  </div>
                }
              >
                <Input placeholder={t('policy.form.process_name.placeholder', )} />
              </Form.Item>
            </Col>
          </Row>
          


        </Form>
      </Modal>

      {/* 策略统计模态框 */}
      <Modal
        title={t('policy.stats.modal.title', { name: selectedPolicy?.name || '' })}
        open={statsModalVisible}
        onCancel={() => setStatsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setStatsModalVisible(false)}>
            {t('common.close', )}
          </Button>
        ]}
        width="90%"
        style={{ top: 20, maxWidth: '1400px' }}
        destroyOnHidden
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto', padding: '16px' } }}
      >
        {policyStats && (
          <div>
            <Card title={t('policy.stats.card.basic_info.title', )} style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}>
                      {policyStats.policy_type || 'N/A'}
                    </div>
                    <div>{t('policy.stats.basic.type', )}</div>
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: policyStats.status === 'active' ? '#52c41a' : '#ff4d4f' }}>
                      {policyStats.status === 'active' ? t('policy.status.enabled', ) : t('policy.status.disabled', )}
                    </div>
                    <div>{t('policy.stats.basic.status', )}</div>
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#722ed1' }}>
                      {new Date(policyStats.created_at || Date.now()).toLocaleDateString()}
                    </div>
                    <div>{t('policy.stats.basic.created_date', )}</div>
                  </div>
                </Col>
              </Row>
            </Card>
            <Card title={t('policy.stats.card.detail.title', )} style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                      {policyStats.rule_count || 0}
                    </div>
                    <div>{t('policy.stats.detail.rule_total', )}</div>
                  </div>
                </Col>
              </Row>
              {policyStats.rule_details && policyStats.rule_details.length > 0 ? (
                <div>
                  {policyStats.rule_details.map((rule, index) => (
                    <Card key={rule.id} size="small" style={{ marginBottom: 8 }} title={t('policy.stats.detail.rule_title', { index: index + 1, name: rule.name })}>
                      <Row gutter={[16, 8]}>
                        <Col span={6}>
                          <Text strong>{t('policy.stats.detail.type', )}</Text>
                          <Tag color={rule.type === 'whitelist' ? 'green' : 'red'}>
                            {rule.type === 'whitelist' ? t('policy.rule.type.whitelist', ) : t('policy.rule.type.blacklist', )}
                          </Tag>
                        </Col>
                        <Col span={6}>
                          <Text strong>{t('policy.stats.detail.category', )}</Text> {rule.category}
                        </Col>
                        <Col span={6}>
                          <Text strong>{t('policy.stats.detail.priority', )}</Text> {rule.priority}
                        </Col>
                        <Col span={6}>
                          <Text strong>{t('policy.stats.detail.status', )}</Text>
                          <Tag color={rule.status === 'active' ? 'green' : 'default'}>
                            {rule.status === 'active' ? t('policy.status.enabled', ) : t('policy.status.disabled', )}
                          </Tag>
                        </Col>
                      </Row>
                      {rule.description && (
                        <Row style={{ marginTop: 8 }}>
                          <Col span={24}>
                            <Text strong>{t('policy.stats.detail.description', )}</Text> {rule.description}
                          </Col>
                        </Row>
                      )}
                      {rule.conditions && rule.conditions.length > 0 && (
                        <Row style={{ marginTop: 8 }}>
                          <Col span={24}>
                            <Text strong>{t('policy.stats.detail.conditions', )}</Text>
                            <div style={{ marginTop: 4 }}>
                              {rule.conditions.map((condition, idx) => (
                                <Tag key={idx} style={{ marginBottom: 4 }}>
                                  {condition.field} {condition.operator} {condition.value}
                                </Tag>
                              ))}
                            </div>
                          </Col>
                        </Row>
                      )}
                      {rule.actions && rule.actions.length > 0 && (
                        <Row style={{ marginTop: 8 }}>
                          <Col span={24}>
                            <Text strong>{t('policy.stats.detail.actions', )}</Text>
                            <div style={{ marginTop: 4 }}>
                              {rule.actions.map((action, idx) => (
                                <Tag key={idx} color="blue" style={{ marginBottom: 4 }}>
                                  {action.type}
                                </Tag>
                              ))}
                            </div>
                          </Col>
                        </Row>
                      )}
                    </Card>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                  {t('policy.stats.detail.no_rules', )}
                </div>
              )}
            </Card>
            <Card title={t('policy.stats.card.devices.title', )}>
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a' }}>
                      {policyStats.target_device_count || 0}
                    </div>
                    <div>{t('policy.stats.devices.target_count', )}</div>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#13c2c2' }}>
                      {policyStats.push_success_count || 0}
                    </div>
                    <div>{t('policy.stats.devices.push_success', )}</div>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fa8c16' }}>
                      {policyStats.push_pending_count || 0}
                    </div>
                    <div>{t('policy.stats.devices.push_pending', )}</div>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ff4d4f' }}>
                      {policyStats.push_failed_count || 0}
                    </div>
                    <div>{t('policy.stats.devices.push_failed', )}</div>
                  </div>
                </Col>
              </Row>
              {policyStats.target_group_count > 0 && (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col span={24}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                        {policyStats.target_group_count}
                      </div>
                      <div>{t('policy.stats.devices.target_group_count', )}</div>
                    </div>
                  </Col>
                </Row>
              )}
              {policyStats.target_devices && Array.isArray(policyStats.target_devices) && policyStats.target_devices.length > 0 ? (
                <div>
                  <div style={{ marginBottom: 12, fontWeight: 'bold' }}>
                    {t('policy.stats.devices.detail_title', { count: policyStats.target_devices.length })}
                  </div>
                  <Table
                    dataSource={policyStats.target_devices}
                    rowKey="id"
                    size="small"
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => t('policy.stats.devices.pagination.show_total', { start: range[0], end: range[1], total })
                    }}
                    scroll={{ x: 'max-content' }}
                    columns={[
                      { title: t('policy.device_table.columns.name', ), dataIndex: 'name', key: 'name', width: 120, ellipsis: false, render: (text, record) => (
                        <div>
                          <div title={text}>{text}</div>
                          <div style={{ fontSize: '12px', color: '#999' }} title={record.hostname}>{record.hostname}</div>
                        </div>
                      ) },
                      { title: t('policy.device_table.columns.serial_number', ), dataIndex: 'serial_number', key: 'serial_number', width: 140, ellipsis: false, render: (text) => (
                        <span title={text || 'N/A'}>{text || 'N/A'}</span>
                      ) },
                      { title: t('policy.device_table.columns.uuid', ), dataIndex: 'uuid', key: 'uuid', width: 120, ellipsis: false, render: (text) => (
                        <span title={text || 'N/A'}>{text || 'N/A'}</span>
                      ) },
                      { title: t('policy.device_table.columns.user', ), dataIndex: 'user_name', key: 'user_name', width: 100, ellipsis: false, render: (text) => (
                        <span title={text || 'N/A'}>{text || 'N/A'}</span>
                      ) },
                      { title: t('policy.device_table.columns.ip_address', ), dataIndex: 'ip_address', key: 'ip_address', width: 120, ellipsis: false, render: (text) => (
                        <span title={text || 'N/A'}>{text || 'N/A'}</span>
                      ) },
                      { title: t('policy.device_table.columns.os', ), dataIndex: 'os', key: 'os', width: 100, ellipsis: false, render: (text, record) => (
                        <div>
                          <div title={text}>{text}</div>
                          <div style={{ fontSize: '12px', color: '#999' }} title={record.os_version}>{record.os_version}</div>
                        </div>
                      ) },
                      { title: t('policy.device_table.columns.agent_version', ), dataIndex: 'agent_version', key: 'agent_version', width: 100, ellipsis: false, render: (text) => (
                        <span title={text || 'N/A'}>{text || 'N/A'}</span>
                      ) },
                      { title: t('policy.device_table.columns.group', ), dataIndex: 'group_name', key: 'group_name', width: 100, ellipsis: false, render: (text) => (
                        <span title={text || 'N/A'}>{text || 'N/A'}</span>
                      ) },
                      { title: t('policy.device_table.columns.online_status', ), dataIndex: 'online_status', key: 'online_status', width: 80, render: (status) => (
                        <Tag color={status === 'online' ? 'green' : 'default'}>
                          {status === 'online' ? t('policy.device_table.status.online', ) : t('policy.device_table.status.offline', )}
                        </Tag>
                      ) },
                      { title: t('policy.device_table.columns.sync_status', ), dataIndex: 'sync_status', key: 'sync_status', width: 80, render: (status) => {
                        const statusConfig = {
                          'success': { color: 'green', text: 'Synchronized' },
                          'pending': { color: 'orange', text: 'Pending' },
                          'failed': { color: 'red', text: 'Push Failed' }
                        };
                        const config = statusConfig[status] || { color: 'default', text: status };
                        return <Tag color={config.color}>{config.text}</Tag>;
                      } },
                      { title: t('policy.device_table.columns.last_sync_time', ), dataIndex: 'last_sync_time', key: 'last_sync_time', width: 140, render: (time) => time || t('policy.device_table.last_sync_time.none', ) }
                    ]}
                  />
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                  {policyStats.target_device_count && policyStats.target_device_count > 0 ? (
                    <div>
                      <div>{t('policy.stats.devices.detail_configured', { count: policyStats.target_device_count })}</div>
                      <div style={{ fontSize: '12px', marginTop: '8px' }}>
                        {t('policy.stats.devices.detail_loading_or_empty', )}
                      </div>
                    </div>
                  ) : (
                    t('policy.stats.devices.no_targets', )
                  )}
                </div>
              )}
            </Card>
          </div>
        )}
      </Modal>


    </div>
  );
};

export default PolicyManagement;
